/**
 * MilestoneItem Component
 *
 * Displays a single milestone with status dropdown and details
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Select, MenuItem, FormControl } from '@mui/material';
import { Flag, Edit, Delete } from '@mui/icons-material';
import { Milestone } from '../../../types';
import { COMPLETION_STATUS_OPTIONS, getStatusColor } from '../utils/scoreUtils';

export interface MilestoneItemProps {
    milestone: Milestone;
    isDarkMode: boolean;
    onStatusChange?: (milestone: Milestone, newStatus: string) => void;
    onEdit?: (milestone: Milestone) => void;
    onDelete?: (milestone: Milestone) => void;
    showActions?: boolean;
    compact?: boolean;
}

export const MilestoneItem: React.FC<MilestoneItemProps> = ({
    milestone,
    isDarkMode,
    onStatusChange,
    onEdit,
    onDelete,
    showActions = false,
    compact = false,
}) => {
    // Optimistic UI state for immediate visual feedback
    const [optimisticStatus, setOptimisticStatus] = useState<string | null>(null);

    // Use optimistic status if set, otherwise use prop
    const displayStatus = optimisticStatus ?? (milestone.status || 'not_started');
    const statusColor = getStatusColor(displayStatus);
    const isCompleted = displayStatus === 'completed';

    // Reset optimistic state when prop changes (e.g., from parent refetch)
    useEffect(() => {
        setOptimisticStatus(null);
    }, [milestone.status]);

    const handleStatusChange = async (event: any) => {
        if (onStatusChange) {
            const newStatus = event.target.value as string;
            setOptimisticStatus(newStatus); // Immediate visual update
            await onStatusChange(milestone, newStatus);
            setOptimisticStatus(null); // Clear after sync complete
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: compact ? 1 : 2,
                py: compact ? 0.5 : 1,
                px: compact ? 1 : 2,
                borderRadius: 2,
                bgcolor: isDarkMode ? '#1e293b40' : '#f8fafc',
                border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                    bgcolor: isDarkMode ? '#1e293b80' : '#f1f5f9',
                },
            }}
        >
            {/* Status Dropdown */}
            <FormControl size="small" sx={{ minWidth: compact ? 100 : 120 }}>
                <Select
                    value={displayStatus}
                    onChange={handleStatusChange}
                    disabled={!onStatusChange}
                    sx={{
                        fontSize: compact ? '0.75rem' : '0.8rem',
                        height: compact ? 28 : 32,
                        bgcolor: isDarkMode ? '#1e293b' : '#fff',
                        color: statusColor,
                        fontWeight: 600,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: `${statusColor}50`,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: statusColor,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: statusColor,
                        },
                        '& .MuiSelect-icon': {
                            color: statusColor,
                        },
                    }}
                >
                    {COMPLETION_STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value} sx={{ color: opt.color }}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Milestone Icon */}
            <Flag
                sx={{
                    color: statusColor,
                    fontSize: compact ? 18 : 20,
                }}
            />

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: compact ? '0.85rem' : '0.9rem',
                        fontWeight: 500,
                        color: isDarkMode ? '#f1f5f9' : '#1e293b',
                        textDecoration: isCompleted ? 'line-through' : 'none',
                        opacity: isCompleted ? 0.7 : 1,
                    }}
                >
                    {milestone.title}
                </Typography>
                {milestone.due_date && !compact && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: isDarkMode ? '#94a3b8' : '#64748b',
                        }}
                    >
                        Due: {new Date(milestone.due_date).toLocaleDateString()}
                    </Typography>
                )}
            </Box>

            {/* Score */}
            {milestone.score !== undefined && (
                <Typography
                    sx={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: statusColor,
                        minWidth: 40,
                        textAlign: 'right',
                    }}
                >
                    {Math.round((milestone.score || 0) * 100)}%
                </Typography>
            )}

            {/* Actions */}
            {showActions && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {onEdit && (
                        <Tooltip title="Edit">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit(milestone);
                                }}
                                sx={{
                                    color: isDarkMode ? '#94a3b8' : '#64748b',
                                    '&:hover': {
                                        bgcolor: isDarkMode ? '#334155' : '#e2e8f0',
                                    },
                                }}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {onDelete && (
                        <Tooltip title="Delete">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(milestone);
                                }}
                                sx={{
                                    color: '#ef4444',
                                    '&:hover': {
                                        bgcolor: '#ef444420',
                                    },
                                }}
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default MilestoneItem;
